import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { isRTL } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";

const BASE_URL = "https://olyinvestment.com";

export default function BlogDetailPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const rtl = isRTL(i18n.language);
  const currentLang = i18n.language;

  // Placeholder blog verisi
  const blogPost = {
    title: t("blog_detail.placeholder_title"),
    excerpt: t("blog_detail.placeholder_excerpt"),
    author: "OlyInvestment",
    date: "2026-03-15",
    readTime: "5",
    category: t("blog_page.categories.investment"),
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop"
  };

  // SEO metadata
  const seoTitle = `${blogPost.title} - OlyInvestment Blog`;
  const seoDesc = blogPost.excerpt;
  const canonicalUrl = `${BASE_URL}/${currentLang}/blog/${slug}`;
  const publishedDate = new Date(blogPost.date).toISOString();

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet>
        <html lang={currentLang} dir={rtl ? "rtl" : "ltr"} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={blogPost.image} />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:author" content={blogPost.author} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Structured Data - Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogPost.title,
            "description": blogPost.excerpt,
            "image": blogPost.image,
            "datePublished": publishedDate,
            "dateModified": publishedDate,
            "author": {
              "@type": "Organization",
              "name": blogPost.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "OlyInvestment",
              "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo.png`
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={blogPost.image} alt={blogPost.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/70 to-[#0A192F]/50" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Breadcrumb 
            items={[
              { label: t("breadcrumb.blog"), href: `/${currentLang}/blog` },
              { label: blogPost.title }
            ]} 
          />
          
          <div className="mt-8">
            <span 
              className="inline-block px-4 py-1.5 bg-[#D4AF37] text-white text-xs font-medium rounded-sm mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {blogPost.category}
            </span>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl text-white font-bold tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {blogPost.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span style={{ fontFamily: "'Outfit', sans-serif" }}>{blogPost.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={blogPost.date} style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {new Date(blogPost.date).toLocaleDateString(currentLang, { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {blogPost.readTime} {t("blog_detail.min_read")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* İçerik */}
      <article className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Sosyal Paylaşım */}
          <div className="flex items-center justify-between mb-12 pb-8 border-b border-[#E2E8F0]">
            <Link
              to={`/${currentLang}/blog`}
              className="flex items-center gap-2 text-[#4A5568] hover:text-[#D4AF37] transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <ArrowLeft className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
              {t("blog_detail.back_to_blog")}
            </Link>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Share2 className="w-4 h-4 inline-block mr-2" />
                {t("blog_detail.share")}
              </span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${canonicalUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8F9FA] text-[#4A5568] hover:bg-[#1877F2] hover:text-white transition-colors"
                aria-label="Facebook'ta paylaş"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${canonicalUrl}&text=${encodeURIComponent(blogPost.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8F9FA] text-[#4A5568] hover:bg-[#1DA1F2] hover:text-white transition-colors"
                aria-label="Twitter'da paylaş"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${canonicalUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8F9FA] text-[#4A5568] hover:bg-[#0077B5] hover:text-white transition-colors"
                aria-label="LinkedIn'de paylaş"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* İçerik Alanı */}
          <div className="prose prose-lg max-w-none">
            <div className="text-center py-20">
              <p 
                className="text-[#4A5568] text-lg leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {t("blog_detail.content_placeholder")}
              </p>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
